import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Tournament,
  TournamentCaptureMode,
  TournamentCompetitionFormat,
  TournamentParticipationMode,
  TournamentPlatformConfig,
  TournamentPrizeDistribution,
  TournamentScoringMode,
  TournamentSubmissionCapturePolicy,
  TournamentTier,
  TournamentVisibility,
  TournamentScale,
} from '../../types';
import {
  checkTournamentNameAvailability,
  createTournamentDraft,
  updateTournamentDraft,
  submitTournamentForApproval,
} from '../../services/tournaments';
import { updateTournamentLiveDetails } from '../../services/tournamentDetails';
import { deleteTournamentCover, uploadTournamentCover } from '../../services/storage';
import TournamentLocationPicker from './TournamentLocationPicker';
import {
  RODDO_QUICK_TOURNAMENT_TEMPLATES,
  TOURNAMENT_DURATION_PRESETS,
  TOURNAMENT_FORMAT_PRESETS,
  TOURNAMENT_SCALE_OPTIONS,
} from '../../constants/tournamentPresets';

type ToastFn = (message: string, type?: any) => void;

type Props = {
  draft?: Tournament | null;
  userId: string;
  userName?: string;
  config: TournamentPlatformConfig;
  isAdmin: boolean;
  onCancel: () => void;
  onSaved: (tournamentId: string) => void;
  addToast: ToastFn;
};

type DraftForm = {
  name: string;
  description: string;
  tier: TournamentTier;
  participationMode: TournamentParticipationMode;
  visibility: TournamentVisibility;
  tournamentScale: TournamentScale;
  groupName: string;
  coverImageZoom: number;
  coverImagePositionX: number;
  coverImagePositionY: number;
  teamSize: number;
  registrationOpensAt: string;
  registrationClosesAt: string;
  openRegistrationOnApproval: boolean;
  startDate: string;
  endDate: string;
  timezone: string;
  locationScope: 'worldwide' | 'country' | 'region' | 'water' | 'custom';
  locationLabel: string;
  mapLocation: Tournament['mapLocation'];
  competitionFormat: TournamentCompetitionFormat;
  scoringMode: TournamentScoringMode;
  eligibleSpeciesText: string;
  catchLimit: number;
  minQualifyingSize: number;
  measurementUnit: 'in' | 'cm' | 'lb' | 'kg' | 'points';
  captureMode: TournamentCaptureMode;
  catchDisposition: 'release' | 'harvest' | 'either';
  lengthBoardRequired: boolean;
  scaleRequired: boolean;
  tournamentCodeRequired: boolean;
  releaseVideoRequired: boolean;
  locationVerification: 'none' | 'approximate' | 'required';
  entryFeeShells: number;
  prizeDistribution: TournamentPrizeDistribution;
  verifiedSubmissionsEnabled: boolean;
  submissionCapturePolicy: TournamentSubmissionCapturePolicy;
  maxParticipants: number;
  participantApprovalRequired: boolean;
  crewOnly: boolean;
  rulesText: string;
  allowedMethodsText: string;
  disallowedMethodsText: string;
  disqualificationPolicy: string;
  disputeWindowHours: number;
};



const toInputDateTime = (value?: number | null) => {
  if (!value || !Number.isFinite(value)) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const toMillis = (value: string) => {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const defaultForm = (draft?: Tournament | null): DraftForm => {
  const now = Date.now();
  return {
    name: draft?.name || '',
    description: draft?.description || '',
    tier: draft?.tier || 'community',
    participationMode: draft?.participationMode || 'solo',
    visibility: draft?.visibility || 'private',
    tournamentScale: ((draft as any)?.tournamentScale || 'friend_group') as TournamentScale,
    groupName: String((draft as any)?.groupName || ''),
    coverImageZoom: Math.min(1.6, Math.max(1, Number(draft?.coverImageZoom || 1))),
    coverImagePositionX: Math.min(100, Math.max(0, Number(draft?.coverImagePositionX ?? 50))),
    coverImagePositionY: Math.min(100, Math.max(0, Number(draft?.coverImagePositionY ?? 50))),
    teamSize: draft?.participationMode === 'team' ? 2 : 1,
    registrationOpensAt: toInputDateTime(draft?.registrationOpensAt || now + 60 * 60_000),
    registrationClosesAt: toInputDateTime(draft?.registrationClosesAt || now + 24 * 60 * 60_000),
    openRegistrationOnApproval: draft?.openRegistrationOnApproval ?? false,
    startDate: toInputDateTime(draft?.startDate || now + 2 * 24 * 60 * 60_000),
    endDate: toInputDateTime(draft?.endDate || now + 3 * 24 * 60 * 60_000),
    timezone: draft?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    locationScope: draft?.locationScope || 'water',
    locationLabel: draft?.locationLabel || '',
    mapLocation: draft?.mapLocation || null,
    competitionFormat: draft?.competitionFormat || 'biggest_fish',
    scoringMode: draft?.scoringMode || 'longest',
    eligibleSpeciesText: (draft?.eligibleSpecies || []).join(', '),
    catchLimit: draft?.catchLimit || 5,
    minQualifyingSize: draft?.minQualifyingSize || 0,
    measurementUnit: draft?.measurementUnit || 'in',
    captureMode: draft?.captureMode || 'live_photos',
    catchDisposition: draft?.catchDisposition || 'release',
    lengthBoardRequired: draft?.lengthBoardRequired ?? true,
    scaleRequired: draft?.scaleRequired ?? false,
    tournamentCodeRequired: draft?.tournamentCodeRequired ?? true,
    releaseVideoRequired: draft?.releaseVideoRequired ?? false,
    locationVerification: draft?.locationVerification || 'approximate',
    entryFeeShells: draft?.entryFeeShells || 0,
    prizeDistribution: draft?.prizeDistribution || (draft?.participationMode === 'team' ? 'team_split' : 'winner_take_all'),
    verifiedSubmissionsEnabled: draft?.verifiedSubmissionsEnabled ?? true,
    submissionCapturePolicy: draft?.submissionCapturePolicy || 'live_only',
    maxParticipants: draft?.maxParticipants || 50,
    participantApprovalRequired: draft?.participantApprovalRequired ?? false,
    crewOnly: draft?.crewOnly ?? false,
    rulesText: draft?.rulesText || '',
    allowedMethodsText: (draft?.allowedMethods || []).join('\n'),
    disallowedMethodsText: (draft?.disallowedMethods || []).join('\n'),
    disqualificationPolicy: draft?.disqualificationPolicy || '',
    disputeWindowHours: draft?.disputeWindowHours || 24,
  };
};

const fieldClass =
  'roddo-mobile-form w-full rounded-[12px] border border-black/10 bg-white/60 px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none transition focus:border-[#2F80ED]/50 focus:ring-2 focus:ring-[#2F80ED]/15 dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020] dark:text-white dark:focus:border-[#7057E8]/55 dark:focus:ring-[#7057E8]/15';
const labelClass = 'mb-2 block text-[12px] font-black text-slate-700 dark:text-slate-200';
const helpClass = 'mt-2 text-[11px] font-semibold leading-relaxed opacity-55';

const tournamentScopeEmoji = (value: string, label = '') => {
  const key = `${String(value || '').toLowerCase()} ${String(label || '').toLowerCase()}`;
  if (key.includes('global') || key.includes('worldwide')) return '🌎';
  if (
    key.includes('city') ||
    key.includes('province') ||
    key.includes('state') ||
    key.includes('island') ||
    key.includes('country') ||
    key.includes('region')
  ) return '📍';
  return '👥';
};

const tournamentScaleDisplayLabel = (value: string, label: string) =>
  `${tournamentScopeEmoji(value, label)} ${label}`;

const friendlyTimezoneLabel = (value: string) => {
  const clean = String(value || '').trim();
  if (!clean) return 'Local time';
  if (clean === 'UTC') return 'UTC';
  const parts = clean.split('/');
  return (parts[parts.length - 1] || clean).replaceAll('_', ' ');
};

const lines = (value: string) =>
  value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 100);

export default function TournamentDraftWizard({
  draft,
  userId,
  userName,
  config: _config,
  isAdmin,
  onCancel,
  onSaved,
  addToast,
}: Props) {
  const [form, setForm] = useState<DraftForm>(() => defaultForm(draft));
  const [busy, setBusy] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [nameAvailability, setNameAvailability] = useState<
    'idle' | 'checking' | 'available' | 'taken' | 'error'
  >('idle');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(
    draft?.coverImageUrl || draft?.coverThumbUrl || null,
  );
  const [coverRemoved, setCoverRemoved] = useState(false);
  const coverDragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPositionX: number;
    startPositionY: number;
    width: number;
    height: number;
  } | null>(null);
  const [coverDragging, setCoverDragging] = useState(false);

  const editStatus = String(draft?.status || 'draft');
  const editApprovalStatus = String(draft?.approvalStatus || 'not_submitted');
  const isLifecycleEditor =
    !!draft?.id &&
    !(
      editStatus === 'draft' ||
      editApprovalStatus === 'changes_requested'
    );

  const hasStarted =
    isLifecycleEditor &&
    (
      editStatus === 'active' ||
      editStatus === 'live' ||
      (Number(draft?.startDate || 0) > 0 && Date.now() >= Number(draft?.startDate || 0))
    );

  const hasParticipantCommitment =
    isLifecycleEditor &&
    (
      Math.max(0, Number(draft?.participantCount || 0)) +
        Math.max(0, Number(draft?.pendingParticipantCount || 0)) +
        Math.max(0, Number(draft?.approvedParticipantCount || 0)) >
        0 ||
      Math.max(0, Number(draft?.shellGrossEntries || 0)) > 0
    );

  // Once approved, tournament identity/format stays stable. The backend mirrors
  // these locks so this is not merely client-side protection.
  const structureLocked = isLifecycleEditor;
  const scheduleLocked = isLifecycleEditor && hasStarted;
  const locationLocked = isLifecycleEditor && hasStarted;
  const competitionLocked =
    isLifecycleEditor && (hasStarted || hasParticipantCommitment);

  const lockSurface =
    'rounded-[14px] border border-black/8 bg-black/[.018] p-3 opacity-60 dark:border-white/[.07] dark:bg-white/[.025]';

  useEffect(() => {
    const name = form.name.trim();

    if (name.length < 3) {
      setNameAvailability('idle');
      return;
    }

    if (
      draft?.id &&
      name.localeCompare(String(draft.name || '').trim(), undefined, {
        sensitivity: 'accent',
      }) === 0
    ) {
      setNameAvailability('available');
      return;
    }

    let cancelled = false;
    setNameAvailability('checking');

    const timer = window.setTimeout(() => {
      void checkTournamentNameAvailability(name, draft?.id || null)
        .then((result) => {
          if (cancelled) return;
          setNameAvailability(result.available ? 'available' : 'taken');
        })
        .catch((error) => {
          if (cancelled) return;
          console.warn('[TournamentDraftWizard] name availability check failed', error);
          setNameAvailability('error');
        });
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.name, draft?.id, draft?.name]);

  useEffect(() => {
    return () => {
      if (coverPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(coverPreviewUrl);
    };
  }, [coverPreviewUrl]);

  const chooseCover = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast('Choose an image file for the tournament cover.', 'error');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      addToast('Choose an image smaller than 20 MB.', 'error');
      return;
    }

    if (coverPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(coverPreviewUrl);
    setCoverFile(file);
    setCoverPreviewUrl(URL.createObjectURL(file));
    setCoverRemoved(false);
  };

  const removeCover = () => {
    if (coverPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(coverPreviewUrl);
    setCoverFile(null);
    setCoverPreviewUrl(null);
    setCoverRemoved(true);
  };

  const beginCoverDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!coverPreviewUrl) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    coverDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPositionX: form.coverImagePositionX,
      startPositionY: form.coverImagePositionY,
      width: rect.width,
      height: rect.height,
    };
    setCoverDragging(true);
  };

  const moveCoverDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = coverDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();

    // Direct-manipulation feel: dragging the photo right/down moves the photo
    // right/down, which means the focal point moves left/up.
    const deltaX = ((event.clientX - drag.startClientX) / drag.width) * 100;
    const deltaY = ((event.clientY - drag.startClientY) / drag.height) * 100;

    setForm((prev) => ({
      ...prev,
      coverImagePositionX: Math.min(100, Math.max(0, drag.startPositionX - deltaX)),
      coverImagePositionY: Math.min(100, Math.max(0, drag.startPositionY - deltaY)),
    }));
  };

  const endCoverDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = coverDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {}
    coverDragRef.current = null;
    setCoverDragging(false);
  };

  const set = <K extends keyof DraftForm>(key: K, value: DraftForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyScale = (scale: TournamentScale) => {
    const preset = TOURNAMENT_SCALE_OPTIONS.find((item) => item.value === scale);
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      tournamentScale: scale,
      tier: prev.tier === 'roddo_official' ? prev.tier : preset.tier,
      visibility: preset.visibility,
      participationMode: preset.participationMode || prev.participationMode,
      teamSize: preset.participationMode === 'team' ? 2 : prev.teamSize,
      locationScope: preset.locationScope || prev.locationScope,
      mapLocation: preset.locationScope === 'worldwide' ? null : prev.mapLocation,
      crewOnly: preset.visibility === 'crew' ? true : prev.crewOnly,
    }));
  };

  const applyFormatPreset = (presetKey: string) => {
    const preset = TOURNAMENT_FORMAT_PRESETS.find((item) => item.key === presetKey);
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      competitionFormat: preset.competitionFormat,
      scoringMode: preset.scoringMode,
      participationMode: preset.participationMode || prev.participationMode,
      teamSize: preset.participationMode === 'team' ? 2 : prev.teamSize,
      scaleRequired: preset.scoringMode === 'heaviest' ? true : prev.scaleRequired,
      lengthBoardRequired: ['longest', 'best_3', 'best_5', 'team_total'].includes(preset.scoringMode)
        ? true
        : prev.lengthBoardRequired,
    }));
  };


  const applyQuickTemplate = (templateKey: string) => {
    const preset = RODDO_QUICK_TOURNAMENT_TEMPLATES.find((item) => item.key === templateKey);
    if (!preset || structureLocked) return;

    setForm((prev) => ({
      ...prev,
      name: prev.name.trim() ? prev.name : preset.label,
      competitionFormat: preset.competitionFormat,
      scoringMode: preset.scoringMode,
      participationMode: 'solo',
      teamSize: 1,
      eligibleSpeciesText: (preset.eligibleSpecies || []).join(', '),
      catchLimit: preset.catchLimit || prev.catchLimit,
      measurementUnit: preset.scoringMode === 'most_catches' || preset.scoringMode === 'species_count'
        ? 'points'
        : 'in',
      scaleRequired: false,
      lengthBoardRequired: preset.scoringMode === 'longest',
      verifiedSubmissionsEnabled: true,
      captureMode: 'live_photos',
      submissionCapturePolicy: 'live_only',
    }));
  };

  const applyDurationPreset = (durationMs: number) => {
    if (scheduleLocked) return;
    const startMs = toMillis(form.startDate);
    if (!startMs || !Number.isFinite(durationMs) || durationMs <= 0) return;
    set('endDate', toInputDateTime(startMs + durationMs));
  };



  const validation = useMemo(() => {
    const errors: string[] = [];
    if (form.name.trim().length < 3) errors.push('Tournament name must be at least 3 characters.');
    if (nameAvailability === 'taken') errors.push('That tournament name is already in use. Choose a different name.');
    if (!form.description.trim()) errors.push('Add a short tournament description.');
    if (!form.locationLabel.trim() && form.locationScope !== 'worldwide') errors.push('Add a location or water label.');
    if (
      form.visibility === 'public' &&
      form.locationScope !== 'worldwide' &&
      (!form.mapLocation ||
        !Number.isFinite(Number(form.mapLocation.lat)) ||
        !Number.isFinite(Number(form.mapLocation.lng)))
    ) {
      errors.push('Choose and confirm where this public tournament should appear on Big Blue.');
    }

    const registrationOpen = toMillis(form.registrationOpensAt);
    const registrationClose = toMillis(form.registrationClosesAt);
    const start = toMillis(form.startDate);
    const end = toMillis(form.endDate);

    if (!registrationOpen || !registrationClose || !start || !end) errors.push('Complete all schedule fields.');
    if (registrationClose && registrationOpen && registrationClose <= registrationOpen) errors.push('Registration must close after it opens.');
    if (start && registrationClose && start < registrationClose) errors.push('Tournament start must be after registration closes.');
    if (end && start && end <= start) errors.push('Tournament end must be after tournament start.');
    if (form.participationMode === 'team' && form.teamSize !== 2) errors.push('Duo tournaments require exactly 2 anglers.');
    if (form.participationMode === 'team' && form.maxParticipants % 2 !== 0) errors.push('Duo tournaments require an even maximum participant count.');
    if (!isAdmin && form.tier === 'roddo_official') errors.push('Only Roddo admins can create Roddo Official tournaments.');
    if (form.verifiedSubmissionsEnabled && ['continuous_video', 'photos_and_video'].includes(form.captureMode)) errors.push('Verified submissions currently support live photos only.');
    if (form.verifiedSubmissionsEnabled && form.releaseVideoRequired) errors.push('Release video is not supported by verified submissions yet.');
    if (
      !Number.isSafeInteger(form.entryFeeShells) ||
      form.entryFeeShells < 0
    ) {
      errors.push('Shell entry fee must be a non-negative whole number.');
    }
    if (!form.rulesText.trim()) errors.push('Add tournament rules before saving.');
    return errors;
  }, [form, isAdmin, nameAvailability]);

  const payload = (): Partial<Tournament> & { name: string } => ({
    schemaVersion: 2,
    name: form.name.trim(),
    description: form.description.trim(),
    directorUid: userId,
    directorName: userName || 'Roddo Director',
    tier: form.tier,
    participationMode: form.participationMode,
    visibility: form.visibility,
    tournamentScale: form.tournamentScale,
    groupName: form.groupName.trim(),
    coverImageZoom: Math.min(1.6, Math.max(1, Number(form.coverImageZoom || 1))),
    coverImagePositionX: Math.min(100, Math.max(0, Number(form.coverImagePositionX ?? 50))),
    coverImagePositionY: Math.min(100, Math.max(0, Number(form.coverImagePositionY ?? 50))),
    teamSize: form.participationMode === 'team' ? 2 : 1,
    registrationOpensAt: toMillis(form.registrationOpensAt),
    registrationClosesAt: toMillis(form.registrationClosesAt),
    openRegistrationOnApproval: form.openRegistrationOnApproval,
    startDate: toMillis(form.startDate),
    endDate: toMillis(form.endDate),
    timezone: form.timezone.trim() || 'UTC',
    locationScope: form.locationScope,
    locationLabel: form.locationLabel.trim(),
    mapLocation:
      form.locationScope === 'worldwide'
        ? null
        : form.mapLocation
          ? {
              lat: Number(form.mapLocation.lat),
              lng: Number(form.mapLocation.lng),
              source: form.mapLocation.source || 'manual',
              label: form.locationLabel.trim(),
            }
          : null,
    competitionFormat: form.competitionFormat,
    scoringMode: form.scoringMode,
    eligibleSpecies: lines(form.eligibleSpeciesText),
    catchLimit: Math.max(1, form.catchLimit),
    minQualifyingSize: Math.max(0, form.minQualifyingSize),
    measurementUnit: form.measurementUnit,
    captureMode: form.captureMode,
    catchDisposition: form.catchDisposition,
    lengthBoardRequired: form.lengthBoardRequired,
    scaleRequired: form.scaleRequired,
    tournamentCodeRequired: form.tournamentCodeRequired,
    releaseVideoRequired: form.releaseVideoRequired,
    locationVerification: form.locationVerification,
    entryFeeShells: Math.max(0, Math.floor(form.entryFeeShells)),
    verifiedSubmissionsEnabled: form.verifiedSubmissionsEnabled,
    submissionCapturePolicy: form.submissionCapturePolicy,
    maxParticipants: Math.max(2, form.maxParticipants),
    participantApprovalRequired: form.participantApprovalRequired,
    crewOnly: form.crewOnly,
    rulesText: form.rulesText.trim(),
    allowedMethods: lines(form.allowedMethodsText),
    disallowedMethods: lines(form.disallowedMethodsText),
    disqualificationPolicy: form.disqualificationPolicy.trim(),
    disputeWindowHours: Math.max(1, form.disputeWindowHours),
    prizeDistribution: form.participationMode === 'team' ? 'team_split' : form.prizeDistribution,
    matchmakingMode: form.participationMode === 'team' ? 'teams_balanced_by_rating' : 'open',
    verificationLevel: 'standard',
  });

  const reviewChecklist = useMemo(() => {
    const scheduleReady = [
      form.registrationOpensAt,
      form.registrationClosesAt,
      form.startDate,
      form.endDate,
    ].every((value) => toMillis(value) > 0);
    const locationReady =
      form.locationScope === 'worldwide' || !!form.locationLabel.trim();
    const pinReady =
      form.visibility !== 'public' ||
      form.locationScope === 'worldwide' ||
      (!!form.mapLocation &&
        Number.isFinite(Number(form.mapLocation.lat)) &&
        Number.isFinite(Number(form.mapLocation.lng)));

    return [
      { label: 'Tournament name', ready: form.name.trim().length >= 3 },
      { label: 'Description', ready: !!form.description.trim() },
      { label: 'Schedule', ready: scheduleReady },
      { label: 'Location', ready: locationReady && pinReady },
      { label: 'Rules', ready: !!form.rulesText.trim() },
    ];
  }, [form]);

  const buildLifecyclePatch = (next: Partial<Tournament> & { name: string }) => ({
    name: String(next.name || '').trim(),
    description: String(next.description || '').trim(),
    visibility: (next.visibility || 'private') as TournamentVisibility,
    groupName: String(next.groupName || '').trim(),
    locationScope: next.locationScope,
    locationLabel: next.locationLabel,
    mapLocation: next.mapLocation ?? null,
    registrationOpensAt: next.registrationOpensAt,
    registrationClosesAt: next.registrationClosesAt,
    startDate: next.startDate,
    endDate: next.endDate,
    scoringMode: next.scoringMode,
    eligibleSpecies: next.eligibleSpecies,
    catchLimit: next.catchLimit,
    minQualifyingSize: next.minQualifyingSize,
    measurementUnit: next.measurementUnit,
    entryFeeShells: next.entryFeeShells,
    submissionCapturePolicy: next.submissionCapturePolicy,
    prizeDistribution: next.prizeDistribution,
    rulesText: next.rulesText,
    coverImageZoom: next.coverImageZoom,
    coverImagePositionX: next.coverImagePositionX,
    coverImagePositionY: next.coverImagePositionY,
  });

  const save = async (submitAfterSave = false) => {
    setSaveAttempted(true);
    if (validation.length) {
      addToast(validation[0], 'error');
      return;
    }

    setBusy(true);
    try {
      const next = payload();

      if (!draft?.id || next.name !== String(draft.name || '').trim()) {
        const availability = await checkTournamentNameAvailability(
          next.name,
          draft?.id || null,
        );
        if (!availability.available) {
          setNameAvailability('taken');
          addToast('That tournament name is already in use. Choose a different name.', 'error');
          return;
        }
      }

      if (isLifecycleEditor && draft?.id) {
        let coverPatch: Record<string, any> = {};

        if (coverFile) {
          coverPatch = await uploadTournamentCover(draft.id, coverFile);
        } else if (
          coverRemoved &&
          (draft.coverImageUrl || draft.coverThumbUrl || draft.coverImagePath)
        ) {
          coverPatch = {
            coverImageUrl: null,
            coverThumbUrl: null,
            coverImagePath: null,
            coverImageUpdatedAt: Date.now(),
          };
        }

        const result = await updateTournamentLiveDetails(draft.id, {
          ...buildLifecyclePatch(next),
          ...coverPatch,
        });

        if (
          coverRemoved &&
          (draft.coverImageUrl || draft.coverThumbUrl || draft.coverImagePath)
        ) {
          await deleteTournamentCover(draft.id);
        }

        addToast(
          result.sentBackForReview
            ? 'Tournament updated and sent back to Roddo for review.'
            : 'Tournament updated.',
          'success',
        );
        onSaved(draft.id);
        return;
      }

      // Draft/create path retains the existing draft callable and cover pipeline.
      const id = draft?.id
        ? (await updateTournamentDraft(draft.id, next), draft.id)
        : await createTournamentDraft(next);

      if (coverFile) {
        const cover = await uploadTournamentCover(id, coverFile);
        await updateTournamentDraft(id, cover);
      } else if (
        coverRemoved &&
        (draft?.coverImageUrl || draft?.coverThumbUrl || draft?.coverImagePath)
      ) {
        await updateTournamentDraft(id, {
          coverImageUrl: null,
          coverThumbUrl: null,
          coverImagePath: null,
          coverImageUpdatedAt: Date.now(),
        });
        await deleteTournamentCover(id);
      }

      if (submitAfterSave) {
        await submitTournamentForApproval(id);
        addToast('Tournament submitted for Roddo approval.', 'success');
      } else {
        addToast(
          draft?.id ? 'Tournament draft updated.' : 'Tournament draft created.',
          'success',
        );
      }
      onSaved(id);
    } catch (error: any) {
      console.error('[TournamentDraftWizard] save failed', error);

      const code = String(error?.code || '').toLowerCase();
      const message = String(error?.message || '');
      const duplicateName =
        code.includes('already-exists') ||
        code.includes('already_exists') ||
        /tournament with this name already exists/i.test(message);

      if (duplicateName) {
        setNameAvailability('taken');
        addToast('That tournament name is already in use. Choose a different name.', 'error');
      } else {
        addToast(message || 'Could not save tournament.', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  const selectedStyle = TOURNAMENT_FORMAT_PRESETS.find(
    (preset) =>
      preset.competitionFormat === form.competitionFormat &&
      preset.scoringMode === form.scoringMode,
  );

  const sectionClass =
    'space-y-5 border-t border-black/8 pt-7 first:border-t-0 first:pt-0 dark:border-[rgba(159,140,220,.16)]';
  const sectionTitle = 'text-[16px] font-black tracking-[-0.01em]';

  return (
    <div className="w-full touch-pan-y pb-[calc(var(--roddo-content-inset-bottom,88px)+32px)]">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[22px] font-black leading-tight">
            {draft ? 'Edit tournament' : 'Create tournament'}
          </h2>
          <p className="mt-1 text-sm font-semibold opacity-60">
            {isLifecycleEditor
              ? 'Update the tournament safely. Locked competition settings stay protected.'
              : 'Set the essentials, review the details, and publish when ready.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-black/10 bg-white/60 text-lg font-black leading-none transition active:translate-y-px dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020]"
          aria-label="Close tournament setup"
          title="Close"
        >
          ×
        </button>
      </div>

      <div className="space-y-7">
        {isLifecycleEditor ? (
          <div className="rounded-[15px] border border-[#2F80ED]/18 bg-[#2F80ED]/8 px-4 py-3 dark:border-[#7057E8]/18 dark:bg-[#7057E8]/8">
            <div className="text-sm font-black">Editing a published tournament</div>
            <div className="mt-1 text-[11px] font-semibold leading-relaxed opacity-60">
              Cover art and public details stay editable. Schedule changes lock after the start.
              Competition changes lock after anglers join and may require Roddo review before the event begins.
            </div>
          </div>
        ) : null}

        {!structureLocked ? (
          <section className="space-y-3 rounded-[16px] border border-[#2F80ED]/15 bg-[#2F80ED]/[.045] p-3.5 dark:border-[#7057E8]/18 dark:bg-[#7057E8]/[.07]">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[.14em] text-[#2168C9] dark:text-violet-200">Quick start</div>
              <div className="mt-1 text-sm font-black">Start with a proven tournament format</div>
              <div className="mt-1 text-[11px] font-semibold opacity-55">These only prefill competition settings. You still control schedule, location, rules, and entry.</div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {RODDO_QUICK_TOURNAMENT_TEMPLATES.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyQuickTemplate(preset.key)}
                  className="rounded-[12px] border border-black/10 bg-white/65 px-3 py-2.5 text-left transition hover:border-[#2F80ED]/35 hover:bg-white dark:border-white/[.08] dark:bg-white/[.04] dark:hover:border-[#7057E8]/40"
                  title={preset.hint}
                >
                  <div className="text-[12px] font-black">{preset.label}</div>
                  <div className="mt-1 line-clamp-2 text-[10px] font-semibold leading-relaxed opacity-50">{preset.hint}</div>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        <section className={sectionClass}>
          <div className={sectionTitle}>Tournament details</div>

          <label className="block">
            <span className={labelClass}>Tournament name</span>
            <input
              className={`${fieldClass} ${
                nameAvailability === 'taken'
                  ? '!border-red-400/60 focus:!border-red-500/70 focus:!ring-red-400/15'
                  : nameAvailability === 'available'
                    ? '!border-emerald-400/40'
                    : ''
              }`}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="SoCal Bass Challenge"
              aria-invalid={nameAvailability === 'taken'}
              aria-describedby="tournament-name-status"
            />
            <div
              id="tournament-name-status"
              className={`mt-2 min-h-[16px] text-[11px] font-bold ${
                nameAvailability === 'taken'
                  ? 'text-red-700 dark:text-red-300'
                  : nameAvailability === 'available'
                    ? 'text-emerald-700/80 dark:text-emerald-300/80'
                    : 'opacity-45'
              }`}
            >
              {nameAvailability === 'checking'
                ? 'Checking tournament name…'
                : nameAvailability === 'taken'
                  ? 'That tournament name is already in use. Try a different name.'
                  : nameAvailability === 'available'
                    ? 'Tournament name is available.'
                    : nameAvailability === 'error'
                      ? 'Name check unavailable right now. Roddo will verify it when you save.'
                      : 'Tournament names must be unique across Roddo.'}
            </div>
          </label>

          <label className="block">
            <span className={labelClass}>Description</span>
            <textarea
              className={`${fieldClass} min-h-20 resize-y`}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="A short description of the event."
            />
          </label>


          <div>
            <div className={labelClass}>Tournament cover <span className="font-semibold opacity-45">optional</span></div>
            <div className="overflow-hidden rounded-[16px] border border-black/10 bg-black/[0.025] dark:border-[rgba(159,140,220,.20)] dark:bg-white/[0.035]">
              <div
                className={`relative aspect-[16/7] w-full overflow-hidden ${coverPreviewUrl ? (coverDragging ? 'cursor-grabbing touch-none' : 'cursor-grab touch-none') : ''}`}
                onPointerDown={beginCoverDrag}
                onPointerMove={moveCoverDrag}
                onPointerUp={endCoverDrag}
                onPointerCancel={endCoverDrag}
                title={coverPreviewUrl ? 'Drag image to reposition' : undefined}
              >
                {coverPreviewUrl ? (
                  <>
                    <img
                      src={coverPreviewUrl}
                      alt="Tournament cover preview"
                      draggable={false}
                      onDragStart={(event) => event.preventDefault()}
                      className="pointer-events-none h-full w-full select-none object-cover transition-transform duration-200"
                      style={{
                        objectPosition: `${form.coverImagePositionX}% ${form.coverImagePositionY}%`,
                        transform: `scale(${form.coverImageZoom})`,
                        transformOrigin: `${form.coverImagePositionX}% ${form.coverImagePositionY}%`,
                      }}
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <div className="pointer-events-none absolute right-3 top-3 rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[10px] font-black text-white/85 backdrop-blur-md">
                      Drag to reposition
                    </div>
                    <div className="pointer-events-none absolute bottom-3 left-3 right-3 text-white">
                      <div className="text-[10px] font-black uppercase tracking-[.12em] text-white/70">Preview</div>
                      <div className="mt-0.5 truncate text-base font-black">{form.name || 'Your tournament'}</div>
                      <div className="mt-0.5 truncate text-[11px] font-semibold text-white/75">{form.locationLabel || 'Tournament location'}</div>
                    </div>
                  </>
                ) : (
                  <div className="grid h-full place-items-center bg-[#2F80ED]/[.035] px-6 text-center dark:bg-[#7057E8]/[.07]">
                    <div>
                      <div className="text-2xl">🎣</div>
                      <div className="mt-1 text-sm font-black">Add an event cover</div>
                      <div className="mt-1 text-[11px] font-semibold opacity-50">Fishing water, the venue, a catch, or event artwork works well.</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-black/10 px-3 py-2.5 dark:border-[rgba(159,140,220,.16)]">
                <div className="text-[10px] font-semibold opacity-50">Compressed automatically for cards + hero.</div>
                <div className="flex gap-2">
                  {coverPreviewUrl ? (
                    <button type="button" onClick={removeCover} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black text-red-700 hover:bg-red-500/10 dark:text-[#EF8B91]">Remove</button>
                  ) : null}
                  <label className="cursor-pointer rounded-lg border border-[#2F80ED]/20 bg-[#2F80ED]/10 px-2.5 py-1.5 text-[11px] font-black text-[#2168C9] hover:bg-[#2F80ED]/15 dark:border-[#7057E8]/20 dark:bg-[#7057E8]/12 dark:text-violet-200">
                    {coverPreviewUrl ? 'Replace image' : 'Choose image'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                      className="hidden"
                      onChange={(event) => {
                        chooseCover(event.target.files?.[0] || null);
                        event.currentTarget.value = '';
                      }}
                    />
                  </label>
                </div>
              </div>
              {coverPreviewUrl ? (
                <div className="border-t border-black/10 px-3 py-3 dark:border-[rgba(159,140,220,.16)]">
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                    <label className="block">
                      <div className="mb-1.5 flex items-center justify-between gap-3 text-[11px] font-black">
                        <span>Image zoom</span>
                        <span className="opacity-45">{Math.round(form.coverImageZoom * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="1.6"
                        step="0.05"
                        value={form.coverImageZoom}
                        onChange={(event) => set('coverImageZoom', Number(event.target.value))}
                        className="w-full accent-[#2F80ED] dark:accent-[#7057E8]"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, coverImageZoom: 1, coverImagePositionX: 50, coverImagePositionY: 50 }))}
                      className="rounded-lg px-2.5 py-1.5 text-[11px] font-black opacity-60 hover:bg-black/[.04] dark:hover:bg-white/[.05]"
                    >
                      Reset
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 rounded-[11px] bg-black/[.03] px-3 py-2 dark:bg-white/[.035]">
                    <div>
                      <div className="text-[11px] font-black">Drag the image to frame it</div>
                      <div className="mt-0.5 text-[10px] font-semibold opacity-45">Move left, right, up, or down directly in the preview.</div>
                    </div>
                    <div className="shrink-0 text-[10px] font-black tabular-nums opacity-45">
                      {Math.round(form.coverImagePositionX)} / {Math.round(form.coverImagePositionY)}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div className={helpClass}>Drag the preview and use zoom to keep the important part of the photo in frame. Roddo keeps the card and hero sizes consistent.</div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelClass}>Audience</span>
              <select
                className={fieldClass}
                value={form.tournamentScale}
                disabled={structureLocked}
                onChange={(e) => applyScale(e.target.value as TournamentScale)}
              >
                {TOURNAMENT_SCALE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {tournamentScaleDisplayLabel(option.value, option.label)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className={labelClass}>Tournament type</span>
              <select
                className={fieldClass}
                value={form.tier}
                disabled={structureLocked}
                onChange={(e) => set('tier', e.target.value as TournamentTier)}
              >
                <option value="crew">Crew</option>
                <option value="community">Community</option>
                {isAdmin ? <option value="roddo_official">Roddo Official</option> : null}
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className={labelClass}>Play style</div>
              <div className="grid grid-cols-2 gap-1 rounded-[13px] bg-black/[0.025] p-1 dark:bg-white/[0.035]">
                {(['solo', 'team'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={structureLocked}
                    onClick={() => setForm((prev) => ({
                      ...prev,
                      participationMode: mode,
                      teamSize: mode === 'team' ? 2 : 1,
                    }))}
                    className={`rounded-[10px] px-3 py-2.5 text-sm font-black transition ${
                      form.participationMode === mode
                        ? 'bg-[#2F80ED] text-white shadow-sm dark:bg-[#7057E8] dark:text-white'
                        : 'opacity-55'
                    }`}
                  >
                    {mode === 'solo' ? 'Solo' : 'Duo'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className={labelClass}>Visibility</div>
              <div className="grid grid-cols-3 gap-1 rounded-[13px] bg-black/[0.025] p-1 dark:bg-white/[0.035]">
                {([
                  ['private', 'Private'],
                  ['crew', 'Crew'],
                  ['public', 'Public'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    disabled={hasStarted}
                    onClick={() => set('visibility', value)}
                    className={`rounded-[10px] px-2 py-2.5 text-[12px] font-black transition ${
                      form.visibility === value
                        ? 'bg-[#2F80ED] text-white shadow-sm dark:bg-[#7057E8] dark:text-white'
                        : 'opacity-55'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelClass}>
                Group / club <span className="font-semibold opacity-45">optional</span>
              </span>
              <input
                className={fieldClass}
                value={form.groupName}
                onChange={(e) => set('groupName', e.target.value)}
                placeholder="Crew, club, or boat name"
              />
            </label>

            {form.participationMode === 'team' ? (
              <div>
                <span className={labelClass}>Duo roster</span>
                <div className={`${fieldClass} flex items-center justify-between`}>
                  <span>2 linked Roddo anglers</span>
                  <span className="text-[10px] font-black uppercase tracking-[0.08em] opacity-45">Fixed</span>
                </div>
                <div className={helpClass}>Duo identity is bound to two Roddo UIDs during registration. Free-text team matching is not used.</div>
              </div>
            ) : null}
          </div>

          {structureLocked ? (
            <div className="text-[11px] font-semibold opacity-45">
              Audience, tournament type, and play style are locked after submission.
            </div>
          ) : null}
        </section>

        <section className={sectionClass}>
          <div className="flex items-center justify-between gap-3">
            <div className={sectionTitle}>Schedule</div>
            {scheduleLocked ? <span className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Locked</span> : null}
          </div>
          <fieldset disabled={scheduleLocked} className={scheduleLocked ? lockSurface : 'contents'}>

          <div className="grid gap-3 sm:grid-cols-2">
            {([
              ['registrationOpensAt', 'Registration opens'],
              ['registrationClosesAt', 'Registration closes'],
              ['startDate', 'Tournament starts'],
              ['endDate', 'Tournament ends'],
            ] as const).map(([key, label]) => (
              <label key={key}>
                <span className={labelClass}>{label}</span>
                <input
                  className={fieldClass}
                  type="datetime-local"
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                />
              </label>
            ))}
          </div>

          <div>
            <div className={labelClass}>Duration</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {TOURNAMENT_DURATION_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyDurationPreset(preset.durationMs)}
                  className="rounded-[11px] border border-black/10 bg-white/55 px-3 py-2 text-[11px] font-black transition hover:border-[#2F80ED]/35 dark:border-white/[.08] dark:bg-white/[.035] dark:hover:border-[#7057E8]/40"
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={helpClass}>Sets the tournament end time from the selected start time.</div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <span className={labelClass}>Time zone</span>
              <div
                className={`${fieldClass} flex items-center justify-between`}
                title={form.timezone}
              >
                <span>{friendlyTimezoneLabel(form.timezone)}</span>
                <span className="text-[10px] font-black uppercase tracking-[0.08em] opacity-40">Auto</span>
              </div>
              <div className={helpClass}>Uses your device time zone automatically.</div>
            </div>

            <label className="flex items-center gap-2.5 self-end px-1 py-2">
              <input
                type="checkbox"
                checked={form.openRegistrationOnApproval}
                onChange={(e) => set('openRegistrationOnApproval', e.target.checked)}
              />
              <span className="text-sm font-bold">Open signups after approval</span>
            </label>
          </div>
          </fieldset>
        </section>

        <section className={sectionClass}>
          <div className="flex items-center justify-between gap-3">
            <div className={sectionTitle}>Location</div>
            {locationLocked ? <span className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Locked</span> : null}
          </div>
          <fieldset disabled={locationLocked} className={locationLocked ? lockSurface : 'contents'}>

          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelClass}>Location scope</span>
              <select
                className={fieldClass}
                value={form.locationScope}
                onChange={(e) => {
                  const nextScope = e.target.value as DraftForm['locationScope'];
                  setForm((prev) => ({
                    ...prev,
                    locationScope: nextScope,
                    mapLocation: nextScope === 'worldwide' ? null : prev.mapLocation,
                  }));
                }}
              >
                <option value="worldwide">Worldwide / online</option>
                <option value="country">Country</option>
                <option value="region">Region</option>
                <option value="water">Specific water</option>
                <option value="custom">Custom area</option>
              </select>
            </label>

            <label>
              <span className={labelClass}>Location</span>
              <input
                className={fieldClass}
                value={form.locationLabel}
                onChange={(e) => set('locationLabel', e.target.value)}
                placeholder="Diamond Valley Lake, California"
                disabled={form.locationScope === 'worldwide'}
              />
            </label>
          </div>

          {form.locationScope !== 'worldwide' ? (
            <div>
              <div className="mb-2 text-xs font-semibold opacity-55">
                General event area only. Catch locations stay private.
              </div>
              <TournamentLocationPicker
                locationLabel={form.locationLabel}
                value={form.mapLocation}
                onChange={(mapLocation) => set('mapLocation', mapLocation)}
                addToast={addToast}
              />
            </div>
          ) : (
            <div className="rounded-[13px] bg-black/[0.025] px-3 py-2.5 text-xs font-semibold opacity-65 dark:bg-white/[0.035]">
              Worldwide tournaments have no fixed geographic pin. Roddo shows them near each eligible angler on Big Blue as a WORLD discovery marker.
            </div>
          )}
          </fieldset>
        </section>

        <section className={`${sectionClass} !space-y-6`}>
          <div className="flex items-center justify-between gap-3">
            <div className={`${sectionTitle} mb-1`}>Competition</div>
            {competitionLocked ? <span className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Locked</span> : null}
          </div>
          <fieldset disabled={competitionLocked} className={competitionLocked ? lockSurface : 'contents'}>

          <label>
            <span className={labelClass}>Style</span>
            <select
              className={fieldClass}
              value={selectedStyle?.key || ''}
              onChange={(e) => applyFormatPreset(e.target.value)}
            >
              {!selectedStyle ? <option value="">Choose a style</option> : null}
              {TOURNAMENT_FORMAT_PRESETS.map((preset) => (
                <option key={preset.key} value={preset.key}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block pt-1">
            <span className={labelClass}>Eligible species</span>
            <input
              className={fieldClass}
              value={form.eligibleSpeciesText}
              onChange={(e) => set('eligibleSpeciesText', e.target.value)}
              placeholder="Leave blank for any species"
            />
          </label>

          <div className="grid gap-4 pt-1 sm:grid-cols-2">
            <label>
              <span className={labelClass}>Catch limit</span>
              <input
                className={fieldClass}
                type="number"
                min={1}
                max={100}
                value={form.catchLimit}
                onChange={(e) => set('catchLimit', Number(e.target.value))}
              />
            </label>

            <label>
              <span className={labelClass}>Minimum qualifying size</span>
              <div className="flex gap-2">
                <input
                  className={fieldClass}
                  type="number"
                  min={0}
                  step="0.1"
                  value={form.minQualifyingSize}
                  onChange={(e) => set('minQualifyingSize', Number(e.target.value))}
                />
                <select
                  className="rounded-[13px] border border-black/10 bg-white/72 px-3 text-sm font-black dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020]"
                  value={form.measurementUnit}
                  onChange={(e) => set('measurementUnit', e.target.value as DraftForm['measurementUnit'])}
                >
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                  <option value="points">pts</option>
                </select>
              </div>
            </label>
          </div>
          </fieldset>
        </section>

        <section className={`${sectionClass} !space-y-4`}>
          <div className="flex items-center justify-between gap-3">
            <div className={sectionTitle}>Verification</div>
            {competitionLocked ? <span className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Locked</span> : null}
          </div>
          <fieldset disabled={competitionLocked} className={competitionLocked ? lockSurface : 'contents'}>

          <div className="grid gap-x-3 gap-y-2.5 sm:grid-cols-2">
            <label>
              <span className={labelClass}>Photo verification</span>
              <select
                className={fieldClass}
                value={form.submissionCapturePolicy}
                onChange={(e) =>
                  set('submissionCapturePolicy', e.target.value as TournamentSubmissionCapturePolicy)
                }
              >
                <option value="live_only">Live capture only</option>
                <option value="live_or_upload">Live capture or uploaded photo</option>
              </select>
            </label>

            <label>
              <span className={labelClass}>Catch handling</span>
              <select
                className={fieldClass}
                value={form.catchDisposition}
                onChange={(e) => set('catchDisposition', e.target.value as DraftForm['catchDisposition'])}
              >
                <option value="release">Catch, photo, release</option>
                <option value="harvest">Harvest event</option>
                <option value="either">Either, where legal</option>
              </select>
            </label>

            <label>
              <span className={labelClass}>Location check</span>
              <select
                className={fieldClass}
                value={form.locationVerification}
                onChange={(e) => set('locationVerification', e.target.value as DraftForm['locationVerification'])}
              >
                <option value="none">None</option>
                <option value="approximate">Approximate private check</option>
                <option value="required">Required private check</option>
              </select>
            </label>

            <label className="flex items-center gap-2.5 px-1 py-1.5">
              <input
                type="checkbox"
                checked={form.verifiedSubmissionsEnabled}
                onChange={(e) => set('verifiedSubmissionsEnabled', e.target.checked)}
              />
              <span className="text-sm font-bold">Allow tournament catch submissions</span>
            </label>
          </div>

          <div className="grid gap-1.5 sm:grid-cols-3">
            {([
              ['lengthBoardRequired', 'Length board'],
              ['scaleRequired', 'Scale'],
              ['tournamentCodeRequired', 'Tournament code'],
            ] as const).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 px-1 py-1 text-[12px] font-bold"
              >
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={(e) => set(key, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          </fieldset>
        </section>

        <section className={sectionClass}>
          <div className="flex items-center justify-between gap-3">
            <div className={sectionTitle}>Entry & prizes</div>
            {competitionLocked ? <span className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Locked</span> : null}
          </div>
          <fieldset disabled={competitionLocked} className={competitionLocked ? lockSurface : 'contents'}>

          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelClass}>Shell entry fee</span>
              <input
                className={fieldClass}
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={form.entryFeeShells}
                onChange={(e) => set('entryFeeShells', Number(e.target.value))}
              />
              <div className={helpClass}>0 = free entry.</div>
            </label>

            <label>
              <span className={labelClass}>Prize distribution</span>
              <select
                className={fieldClass}
                value={form.participationMode === 'team' ? 'team_split' : form.prizeDistribution}
                disabled={form.participationMode === 'team'}
                onChange={(e) => set('prizeDistribution', e.target.value as TournamentPrizeDistribution)}
              >
                {form.participationMode === 'team' ? (
                  <option value="team_split">Winning team split</option>
                ) : (
                  <>
                    <option value="winner_take_all">Winner take all</option>
                    <option value="top_three">Top 3 · 50 / 30 / 20</option>
                    <option value="top_five">Top 5 · 40 / 25 / 15 / 12 / 8</option>
                  </>
                )}
              </select>
            </label>

            <label>
              <span className={labelClass}>Maximum participants</span>
              <input
                className={fieldClass}
                type="number"
                min={2}
                max={5000}
                value={form.maxParticipants}
                onChange={(e) => set('maxParticipants', Number(e.target.value))}
              />
            </label>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex items-center gap-2.5 px-1 py-2">
              <input
                type="checkbox"
                checked={form.participantApprovalRequired}
                onChange={(e) => set('participantApprovalRequired', e.target.checked)}
              />
              <span className="text-sm font-bold">Director approves participants</span>
            </label>

            <label className="flex items-center gap-2.5 px-1 py-2">
              <input
                type="checkbox"
                checked={form.crewOnly}
                onChange={(e) => set('crewOnly', e.target.checked)}
              />
              <span className="text-sm font-bold">Crew members only</span>
            </label>
          </div>
          </fieldset>
        </section>

        <section className={sectionClass}>
          <div className="flex items-center justify-between gap-3">
            <div className={sectionTitle}>Rules</div>
            {competitionLocked ? <span className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Locked</span> : null}
          </div>
          <fieldset disabled={competitionLocked} className={competitionLocked ? lockSurface : 'contents'}>

          <label>
            <span className={labelClass}>Core rules</span>
            <textarea
              className={`${fieldClass} min-h-32 resize-y`}
              value={form.rulesText}
              onChange={(e) => set('rulesText', e.target.value)}
              placeholder="Eligibility, scoring, fish handling, boundaries, deadlines, and sportsmanship."
            />
          </label>

          <div>
            <div className="mb-2 text-[11px] font-black uppercase tracking-[0.12em] opacity-45">Methods</div>
            <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelClass}>Allowed</span>
              <textarea
                className={`${fieldClass} min-h-24 resize-y`}
                value={form.allowedMethodsText}
                onChange={(e) => set('allowedMethodsText', e.target.value)}
                placeholder={'Artificial lures\nOne rod at a time'}
              />
            </label>

            <label>
              <span className={labelClass}>Not allowed</span>
              <textarea
                className={`${fieldClass} min-h-24 resize-y`}
                value={form.disallowedMethodsText}
                onChange={(e) => set('disallowedMethodsText', e.target.value)}
                placeholder={'Live bait\nTrolling'}
              />
            </label>
            </div>
          </div>

          <div>
            <div className="mb-2 text-[11px] font-black uppercase tracking-[0.12em] opacity-45">Disputes</div>
            <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
            <label>
              <span className={labelClass}>Disqualification policy</span>
              <textarea
                className={`${fieldClass} min-h-20 resize-y`}
                value={form.disqualificationPolicy}
                onChange={(e) => set('disqualificationPolicy', e.target.value)}
                placeholder="Late submissions, missing evidence, unsafe handling, or rule violations."
              />
            </label>

            <label>
              <span className={labelClass}>Dispute window</span>
              <div className="flex items-center gap-2">
                <input
                  className={fieldClass}
                  type="number"
                  min={1}
                  max={168}
                  value={form.disputeWindowHours}
                  onChange={(e) => set('disputeWindowHours', Number(e.target.value))}
                />
                <span className="text-sm font-bold opacity-65">hr</span>
              </div>
            </label>
            </div>
          </div>
          </fieldset>
        </section>

        <section className={sectionClass}>
          <div className={sectionTitle}>{isLifecycleEditor ? 'Save changes' : 'Review'}</div>

          {saveAttempted && validation.length ? (
            <div className="rounded-[14px] border border-red-400/25 bg-red-100/25 p-3 text-sm font-semibold text-red-800 dark:text-red-200">
              <div className="font-black">A few things still need attention</div>
              <div className="mt-1 space-y-1">
                {validation.map((error) => (
                  <div key={error}>• {error}</div>
                ))}
              </div>
            </div>
          ) : isLifecycleEditor ? (
            <div className="rounded-[14px] border border-black/10 bg-white/35 p-3 text-sm font-semibold opacity-65 dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020]/60">
              Changes that affect schedule or competition may be returned to Roddo review automatically.
            </div>
          ) : (
            <div className="rounded-[14px] border border-black/10 bg-white/35 p-3 dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020]/60">
              <div className="text-sm font-black">{validation.length ? 'Before you submit' : 'Ready to submit'}</div>
              <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {reviewChecklist.map((item) => (
                  <div key={item.label} className="flex items-center gap-2 text-[12px] font-semibold">
                    <span
                      className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border text-[10px] font-black ${
                        item.ready
                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                          : 'border-black/15 text-transparent dark:border-white/20'
                      }`}
                    >
                      {item.ready ? '✓' : '·'}
                    </span>
                    <span className={item.ready ? 'opacity-70' : 'opacity-55'}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="relative overflow-hidden rounded-[14px] border border-black/10 bg-white/40 px-4 py-3 dark:border-[rgba(159,140,220,.20)] dark:bg-[#0E1020]/70">
            {coverPreviewUrl ? <><img src={coverPreviewUrl} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${form.coverImagePositionX}% ${form.coverImagePositionY}%`, transform: `scale(${form.coverImageZoom})`, transformOrigin: `${form.coverImagePositionX}% ${form.coverImagePositionY}%` }} /><div className="absolute inset-0 bg-black/60" /></> : null}
            <div className={`relative ${coverPreviewUrl ? 'text-white' : ''}`}>
            <div className="text-[16px] font-black">{form.name || 'Untitled tournament'}</div>
            <div className="mt-1 text-[12px] font-semibold opacity-65">
              {selectedStyle?.label || form.scoringMode.replaceAll('_', ' ')} ·{' '}
              {(() => {
                const scale = TOURNAMENT_SCALE_OPTIONS.find((item) => item.value === form.tournamentScale);
                const label = scale?.label || form.tournamentScale.replaceAll('_', ' ');
                return tournamentScaleDisplayLabel(form.tournamentScale, label);
              })()} ·{' '}
              {form.entryFeeShells > 0 ? `${form.entryFeeShells} Shells` : 'Free'}
            </div>
            <div className="mt-0.5 text-[12px] font-semibold opacity-55">
              {form.locationLabel || (form.locationScope === 'worldwide' ? 'Worldwide' : 'Location not set')}
            </div>
            </div>
          </div>
        </section>
      </div>

      <div className="mt-8 border-t border-black/8 pt-5 dark:border-[rgba(159,140,220,.16)]">
        {isLifecycleEditor ? (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={busy}
                onClick={onCancel}
                className="w-full rounded-[13px] border border-black/10 bg-white/55 px-4 py-3 text-sm font-black disabled:opacity-45 sm:w-auto dark:border-white/10 dark:bg-white/[.04]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || nameAvailability === 'checking' || nameAvailability === 'taken'}
                onClick={() => void save(false)}
                className="w-full rounded-[13px] bg-[#2F80ED] px-5 py-3 text-sm font-black text-white shadow-[0_6px_16px_rgba(47,128,237,.18)] disabled:opacity-45 sm:w-auto dark:bg-[#7057E8] dark:shadow-[0_8px_20px_rgba(112,87,232,.20)]"
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] font-semibold opacity-45 sm:text-right">
              Roddo will automatically protect locked tournament settings.
            </p>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={busy || nameAvailability === 'checking' || nameAvailability === 'taken'}
                onClick={() => void save(false)}
                className="w-full rounded-[13px] border border-[#2F80ED]/35 bg-[#2F80ED]/8 px-4 py-3 text-sm font-black text-[#2168C9] disabled:opacity-45 sm:w-auto dark:border-[#7057E8]/35 dark:bg-[#0E1020] dark:text-[#F4F6FA]"
              >
                {busy ? 'Saving…' : draft ? 'Save changes' : 'Save draft'}
              </button>
              <button
                type="button"
                disabled={busy || nameAvailability === 'checking' || nameAvailability === 'taken'}
                onClick={() => void save(true)}
                className="w-full rounded-[13px] border border-[#E5AA22] bg-[linear-gradient(180deg,#FFD96A_0%,#FFC94A_52%,#F6B936_100%)] px-4 py-3 text-sm font-black text-[#2F2410] shadow-[0_5px_14px_rgba(181,126,24,0.14)] disabled:opacity-45 sm:w-auto"
              >
                {busy ? 'Submitting…' : 'Save & submit'}
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] font-semibold opacity-45 sm:text-right">
              Save a draft to finish later, or submit when everything is ready.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
